/**
 * @fileoverview Photos Timeline Overview - Horizontal Month Cards
 *
 * Horizontal scrollable timeline showing photos grouped by month.
 * Each month displayed as a card with photo count and preview grid.
 *
 * @module RenovationProject/Components/PhotosTimelineOverview
 */

import React, { useMemo, useState } from 'react';
import { FileLog } from 'shared/types/file';
import { Image as ImageIcon, CaretRight } from '@phosphor-icons/react';
import { format, startOfMonth } from 'date-fns';
import { nl } from 'date-fns/locale';

interface PhotosTimelineOverviewProps {
  photos: FileLog[];
  projectParts: Array<{ id: number; title: string }>;
}

interface PhotosByMonth {
  [monthKey: string]: {
    date: Date;
    photos: FileLog[];
  };
}

export const PhotosTimelineOverview: React.FC<PhotosTimelineOverviewProps> = ({
  photos,
}) => {
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<FileLog | null>(null);

  // Group photos by month
  const photosByMonth = useMemo(() => {
    const grouped: PhotosByMonth = {};

    photos.forEach((photo) => {
      const photoDate = new Date(photo.documentDate || photo.createdAt || 0);
      const monthStart = startOfMonth(photoDate);
      const monthKey = format(monthStart, 'yyyy-MM');

      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          date: monthStart,
          photos: [],
        };
      }

      grouped[monthKey].photos.push(photo);
    });

    // Sort months descending (newest first)
    const sortedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

    return { grouped, sortedKeys };
  }, [photos]);

  const getPhotoUrl = (photo: FileLog) => {
    if (photo.imageUrl) return photo.imageUrl;
    if (photo.sharePointFileUrl) return photo.sharePointFileUrl;
    return photo.s3Url || '/placeholder-image.jpg';
  };

  const formatMonthHeader = (date: Date) => {
    return format(date, 'MMMM yyyy', { locale: nl });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Geen datum';
    try {
      return format(new Date(dateString), 'd MMM yyyy', { locale: nl });
    } catch {
      return 'Ongeldige datum';
    }
  };

  if (photosByMonth.sortedKeys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <ImageIcon size={48} weight="thin" className="mb-3 text-neutral-400" />
        <p className="text-neutral-600">Geen foto's beschikbaar</p>
        <p className="text-sm text-neutral-400">
          Selecteer projectonderdelen om foto's te zien
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Horizontal Timeline */}
      <div className="relative">
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ scrollbarWidth: 'thin' }}>
          {photosByMonth.sortedKeys.map((monthKey) => {
            const { date, photos: monthPhotos } = photosByMonth.grouped[monthKey];
            const isSelected = selectedMonth === monthKey;

            return (
              <div
                key={monthKey}
                className={`flex-shrink-0 w-[200px] cursor-pointer rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-moss-primary bg-moss-primary/5 shadow-md'
                    : 'border-neutral-200 bg-white hover:border-moss-primary/50 hover:shadow-sm'
                }`}
                onClick={() => setSelectedMonth(isSelected ? null : monthKey)}
              >
                {/* Month Header */}
                <div className="border-b border-neutral-200 p-3">
                  <p className="text-sm font-semibold capitalize text-neutral-primary">
                    {formatMonthHeader(date)}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {monthPhotos.length} foto{monthPhotos.length !== 1 ? "'s" : ''}
                  </p>
                </div>

                {/* Mini Preview Grid (2×2) */}
                <div className="grid grid-cols-2 gap-1 p-2">
                  {monthPhotos.slice(0, 4).map((photo, idx) => (
                    <div
                      key={photo.id}
                      className="aspect-square overflow-hidden rounded bg-neutral-100"
                    >
                      <img
                        src={getPhotoUrl(photo)}
                        alt={`Preview ${idx + 1}`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                  {monthPhotos.length < 4 &&
                    Array.from({ length: 4 - monthPhotos.length }).map((_, idx) => (
                      <div
                        key={`empty-${idx}`}
                        className="aspect-square rounded bg-neutral-100"
                      />
                    ))}
                </div>

                {/* View All Indicator */}
                {monthPhotos.length > 4 && (
                  <div className="border-t border-neutral-200 p-2 text-center">
                    <span className="flex items-center justify-center gap-1 text-xs text-moss-primary">
                      <span>+{monthPhotos.length - 4} meer</span>
                      <CaretRight size={12} weight="bold" />
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Month Full View */}
      {selectedMonth && (
        <div className="mt-6 rounded-lg border-2 border-moss-primary bg-white p-4">
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-lg font-semibold capitalize text-neutral-primary">
              {formatMonthHeader(photosByMonth.grouped[selectedMonth].date)}
            </h4>
            <button
              onClick={() => setSelectedMonth(null)}
              className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            >
              ✕
            </button>
          </div>

          {/* Full Grid */}
          <div className="grid grid-cols-4 gap-3">
            {photosByMonth.grouped[selectedMonth].photos.map((photo) => (
              <div
                key={photo.id}
                className="group relative aspect-square cursor-pointer overflow-hidden rounded-md bg-neutral-100 transition-transform hover:scale-[1.02]"
                onClick={() => setSelectedPhoto(photo)}
              >
                <img
                  src={getPhotoUrl(photo)}
                  alt={Array.isArray(photo.label) ? photo.label.join(', ') : (photo.label || 'Project photo')}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="absolute right-0 bottom-0 left-0 p-2">
                    <p className="truncate text-xs font-medium text-white">
                      {formatDate((photo.documentDate || photo.createdAt) as string)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="relative max-h-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getPhotoUrl(selectedPhoto)}
              alt={Array.isArray(selectedPhoto.label) ? selectedPhoto.label.join(', ') : (selectedPhoto.label || 'Project photo')}
              className="max-h-[90vh] max-w-full rounded-lg object-contain"
            />
            <div className="absolute top-4 right-4 max-w-xs rounded-lg bg-white p-4 shadow-xl">
              <p className="text-neutral-primary mb-2 text-sm font-semibold">
                {formatDate((selectedPhoto.documentDate || selectedPhoto.createdAt) as string)}
              </p>
              {selectedPhoto.label && (
                <p className="text-neutral-secondary-mid mb-1 text-sm">
                  {selectedPhoto.label}
                </p>
              )}
              {selectedPhoto.summary && (
                <p className="text-neutral-secondary-mid mt-2 text-xs">
                  {selectedPhoto.summary}
                </p>
              )}
            </div>
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 left-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-neutral-primary transition-colors hover:bg-neutral-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};
